import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getOllamaConnectionInfo,
  shouldProbeOllamaFromBrowser,
} from "../utils/ollamaClient";

const OLLAMA_STATUS_POLL_INTERVAL_MS = 90000;
const OLLAMA_STATUS_TIMEOUT_MS = 5000;

const OllamaSetupContext = createContext({
  isChecking: true,
  isReachable: false,
  isReady: false,
  hasRequiredModel: false,
  requiredModel: "",
  availableModels: [],
  baseUrl: "",
  error: "",
  lastCheckedAt: null,
  refreshStatus: async () => {},
});

const normalizeModelName = (value) => String(value || "").trim().toLowerCase();

const extractModelNames = (payload = {}) => {
  if (!Array.isArray(payload?.models)) {
    return [];
  }
  return payload.models
    .map((item) => {
      const fromName = String(item?.name || "").trim();
      if (fromName) return fromName;
      return String(item?.model || "").trim();
    })
    .filter(Boolean);
};

export const OllamaSetupProvider = ({ children }) => {
  const connectionInfo = useMemo(() => getOllamaConnectionInfo(), []);
  const shouldProbe = useMemo(
    () => shouldProbeOllamaFromBrowser(connectionInfo.baseUrl),
    [connectionInfo.baseUrl]
  );
  const blockedProbeMessage =
    'Local Ollama checks are only available when this app runs on localhost. Open the local app to use private Ollama features.';
  const [status, setStatus] = useState({
    isChecking: shouldProbe,
    isReachable: false,
    isReady: false,
    hasRequiredModel: false,
    requiredModel: connectionInfo.model,
    availableModels: [],
    baseUrl: connectionInfo.baseUrl,
    error: shouldProbe ? "" : blockedProbeMessage,
    lastCheckedAt: null,
  });

  const refreshStatus = useCallback(async () => {
    if (!shouldProbe) {
      setStatus({
        isChecking: false,
        isReachable: false,
        isReady: false,
        hasRequiredModel: false,
        requiredModel: connectionInfo.model,
        availableModels: [],
        baseUrl: connectionInfo.baseUrl,
        error: blockedProbeMessage,
        lastCheckedAt: new Date().toISOString(),
      });
      return;
    }

    setStatus((previous) => ({ ...previous, isChecking: true }));
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OLLAMA_STATUS_TIMEOUT_MS);

    try {
      const response = await fetch(`${connectionInfo.baseUrl}/api/tags`, {
        method: "GET",
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`Ollama responded with status ${response.status}.`);
      }

      const payload = await response.json();
      const availableModels = extractModelNames(payload);
      const requiredModel = connectionInfo.model;
      const requiredModelNormalized = normalizeModelName(requiredModel);
      const hasRequiredModel = availableModels.some(
        (model) => normalizeModelName(model) === requiredModelNormalized
      );

      setStatus({
        isChecking: false,
        isReachable: true,
        isReady: hasRequiredModel,
        hasRequiredModel,
        requiredModel,
        availableModels,
        baseUrl: connectionInfo.baseUrl,
        error: hasRequiredModel
          ? ""
          : `Connected to Ollama, but required model "${requiredModel}" is not installed.`,
        lastCheckedAt: new Date().toISOString(),
      });
    } catch (error) {
      const message =
        error?.name === "AbortError"
          ? "Timed out while checking Ollama."
          : error?.message || "Unable to connect to Ollama.";
      setStatus({
        isChecking: false,
        isReachable: false,
        isReady: false,
        hasRequiredModel: false,
        requiredModel: connectionInfo.model,
        availableModels: [],
        baseUrl: connectionInfo.baseUrl,
        error: message,
        lastCheckedAt: new Date().toISOString(),
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, [blockedProbeMessage, connectionInfo.baseUrl, connectionInfo.model, shouldProbe]);

  useEffect(() => {
    refreshStatus();
    if (!shouldProbe) {
      return undefined;
    }
    const timerId = setInterval(() => {
      refreshStatus();
    }, OLLAMA_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [refreshStatus, shouldProbe]);

  const value = useMemo(
    () => ({
      ...status,
      refreshStatus,
    }),
    [status, refreshStatus]
  );

  return (
    <OllamaSetupContext.Provider value={value}>
      {children}
    </OllamaSetupContext.Provider>
  );
};

export const useOllamaSetup = () => useContext(OllamaSetupContext);
